import {
  Database,
  ExternalLink,
  LibraryBig,
  Scale,
  ShieldCheck,
  UserCheck,
} from "lucide-react";
import SlideGuide, {
  type GuideSlide,
} from "@/components/training/SlideGuide";
import {
  LONGHAND_OVERVIEW_SLIDES,
  LONGHAND_OVERVIEW_STEPS,
} from "@/data/longhandAIOverview";
import {
  FOUR_GEARS,
  gearRgba,
  type Gear,
} from "@/data/fourGears";

const GEAR_DETAILS: Record<number, {
  unit: string;
  example: string;
  boundary: string;
}> = {
  1: {
    unit: "One question or idea",
    example: "What are the three biggest risks in this plan, based on the attached assumptions?",
    boundary: "A person supplies the context and decides which risk matters now.",
  },
  2: {
    unit: "One finished deliverable",
    example: "Create a board-ready memo from these approved sources and this reporting template.",
    boundary: "A person owns the audience, claims, tone, and approval to share.",
  },
  3: {
    unit: "One multi-step objective",
    example: "Research the competitors, compare the evidence, and produce a review-ready recommendation.",
    boundary: "A person defines scope, permissions, stop rules, and the acceptance decision.",
  },
  4: {
    unit: "One recurring outcome",
    example: "Every Monday, gather current signals and produce a linked priority brief.",
    boundary: "A person owns operating policy, exceptions, and changes to what the loop may do.",
  },
};

const GearsOverviewSlide = () => (
  <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
    <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-300">The Longhand model</p>
    <h1 className="mt-4 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] text-white sm:text-5xl lg:text-6xl">
      Human judgement drives every gear.
    </h1>
    <p className="mt-4 max-w-3xl text-lg leading-relaxed text-slate-300">
      Each gear owns a larger unit of responsibility. The leader still sets direction, defines the boundary, and reviews the result.
    </p>

    <div className="mt-8 border-y border-white/15 py-6">
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {FOUR_GEARS.map((gear) => (
          <div key={gear.n} className="relative border-l-2 pl-4" style={{ borderColor: gear.accent }}>
            <div className="flex items-center gap-3">
              <span
                className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-lg font-black"
                style={{ color: gear.accent, backgroundColor: gearRgba(gear.accent, 0.14) }}
              >
                {gear.n}
              </span>
              <h2 className="text-2xl font-bold text-white">{gear.name}</h2>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-300">{gear.when}</p>
          </div>
        ))}
      </div>
    </div>

    <div className="mt-6 flex items-start gap-3 text-sm leading-relaxed text-slate-300">
      <UserCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-300" />
      <p><strong className="text-white">Shift up only when the unit of work requires it.</strong> Maximum autonomy is not the goal; useful ownership with visible accountability is.</p>
    </div>
  </div>
);

const GearDetailSlide = ({ gear, light }: { gear: Gear; light: boolean }) => {
  const detail = GEAR_DETAILS[gear.n];
  const text = light ? "text-slate-950" : "text-white";
  const muted = light ? "text-slate-600" : "text-slate-300";
  const border = light ? "border-slate-200" : "border-white/15";

  return (
    <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
      <div className="grid items-center gap-8 lg:grid-cols-[0.72fr_1.28fr] lg:gap-14">
        <div>
          <span
            className="flex h-24 w-24 items-center justify-center rounded-full font-serif text-5xl font-bold sm:h-28 sm:w-28 sm:text-6xl"
            style={{ color: gear.accent, backgroundColor: gearRgba(gear.accent, light ? 0.16 : 0.12), border: `2px solid ${gearRgba(gear.accent, 0.55)}` }}
          >
            {gear.n}
          </span>
          <p className={`mt-6 text-xs font-extrabold uppercase tracking-[0.18em] ${muted}`}>Gear {gear.n}</p>
          <h1 className={`mt-2 font-serif text-5xl font-semibold tracking-[-0.04em] sm:text-6xl lg:text-7xl ${text}`}>{gear.name}</h1>
          <p className={`mt-4 text-xl font-semibold ${muted}`}>{detail.unit}</p>
        </div>

        <div className={`border-l-2 pl-6 sm:pl-8 ${border}`}>
          <p className={`text-xs font-extrabold uppercase tracking-[0.18em] ${light ? "text-orange-700" : "text-orange-300"}`}>Use it when</p>
          <p className={`mt-3 text-2xl font-bold leading-snug sm:text-3xl ${text}`}>{gear.when.replace("Use when ", "")}</p>

          <div className={`mt-7 border-y py-5 ${border}`}>
            <p className={`text-xs font-extrabold uppercase tracking-[0.16em] ${muted}`}>Example</p>
            <p className={`mt-3 text-lg leading-relaxed sm:text-xl ${text}`}>&ldquo;{detail.example}&rdquo;</p>
          </div>

          <div className="mt-6 flex items-start gap-3">
            <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0" style={{ color: gear.accent }} />
            <p className={`text-base leading-relaxed ${muted}`}><strong className={text}>Human boundary:</strong> {detail.boundary}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const SkillsVideoSlide = ({ printMode }: { printMode: boolean }) => (
  <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-7 sm:py-10">
    <div className="grid items-center gap-7 lg:grid-cols-[0.82fr_1.18fr] lg:gap-10">
      <div>
        <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-300">The future of work</p>
        <h1 className="mt-4 font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] text-white sm:text-5xl lg:text-6xl">
          Stop building one giant agent.
        </h1>
        <p className="mt-5 text-lg leading-relaxed text-slate-300">
          General agents bring raw intelligence. Agent Skills add the repeatable procedure that turns a brilliant generalist into a reliable professional teammate.
        </p>
        <a
          href="https://www.youtube.com/watch?v=TXiaDHn1dUk"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-sky-300 hover:text-sky-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400"
        >
          The Future of Work 2026 · 7 minutes <ExternalLink className="h-4 w-4" />
        </a>
      </div>

      {printMode ? (
        <div className="flex aspect-video items-center justify-center rounded-3xl border border-white/15 bg-white/[0.06] p-8 text-center">
          <div><LibraryBig className="mx-auto h-12 w-12 text-orange-300" /><p className="mt-4 font-bold text-white">Watch: The Future of Work 2026</p><p className="mt-2 text-sm text-slate-300">youtube.com/watch?v=TXiaDHn1dUk</p></div>
        </div>
      ) : (
        <div className="aspect-video overflow-hidden rounded-3xl border border-white/15 bg-black shadow-2xl shadow-black/35">
          <iframe
            className="h-full w-full"
            src="https://www.youtube-nocookie.com/embed/TXiaDHn1dUk"
            title="The Future of Work 2026: Build AI Skills, Not Agents"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      )}
    </div>
  </div>
);

const SkillAppsSlide = () => (
  <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
    <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-300">A useful computing analogy</p>
    <h1 className="mt-4 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] text-white sm:text-5xl lg:text-6xl">
      Skills are the apps of the AI era.
    </h1>

    <div className="mt-8 divide-y divide-white/15 border-y border-white/15">
      {[
        ["Models", "The processor", "Raw intelligence and reasoning potential."],
        ["Runtime + connections", "The operating system", "The environment, tools, data, permissions, and agent loop."],
        ["Skills", "The applications", "Specialized procedures that package expertise for reliable reuse."],
      ].map(([label, analogy, meaning], index) => (
        <div key={label} className="grid gap-2 py-5 sm:grid-cols-[0.65fr_0.8fr_1.55fr] sm:items-center sm:gap-6">
          <span className="text-sm font-extrabold uppercase tracking-[0.15em] text-orange-300">0{index + 1} · {label}</span>
          <strong className="text-xl text-white">{analogy}</strong>
          <span className="text-base leading-relaxed text-slate-300">{meaning}</span>
        </div>
      ))}
    </div>

    <p className="mt-7 max-w-4xl text-lg leading-relaxed text-slate-200">
      Domain experts can capture how work is really done. The team library compounds, and a new teammate can inherit working knowledge instead of starting from zero.
    </p>
  </div>
);

const DmpSlide = () => (
  <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-7 sm:py-10">
    <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-300">The Decision Making Process</p>
    <h1 className="mt-4 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] text-white sm:text-5xl lg:text-6xl">
      DMP turns possibility into a decision.
    </h1>

    <div className="mt-7 rounded-2xl border border-orange-300/30 bg-orange-300/10 px-5 py-4 text-center">
      <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-orange-200">The Big What</p>
      <p className="mt-2 text-xl font-bold text-white">Name the exact decision, boundary, and timing before debating the answer.</p>
    </div>

    <div className="mt-4 grid gap-4 sm:grid-cols-2">
      {[
        ["Why", "Result and evidence", "What evidence, urgency, and assumptions make this worth attention?"],
        ["What", "Bounded workflow", "Which users, inputs, outputs, boundaries, non-goals, and finish line are in scope?"],
        ["How", "Credible operating model", "Which people, AI, data, checks, permissions, effort, and review make it work?"],
        ["What If", "Decision-changing uncertainty", "Which signals, alternatives, owners, and stop rules could change the choice?"],
      ].map(([quadrant, label, question]) => (
        <div key={quadrant} className="border-l-2 border-sky-300 bg-white/[0.05] p-5">
          <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-sky-300">{quadrant}</p>
          <h2 className="mt-2 text-xl font-bold text-white">{label}</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-300">{question}</p>
        </div>
      ))}
    </div>

    <a href="https://www.youtube.com/watch?v=NQfKHsbHfnA" target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex w-fit items-center gap-2 text-sm font-bold text-sky-300 hover:text-sky-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400">
      Watch the complete Decision Making Process <ExternalLink className="h-4 w-4" />
    </a>
  </div>
);

const TenEightyTenSlide = () => (
  <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-8 sm:py-12">
    <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-300">10 / 80 / 10</p>
    <h1 className="mt-4 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] text-white sm:text-5xl lg:text-6xl">
      Human judgement belongs before, during, and after the run.
    </h1>

    <div className="mt-8 grid gap-4 lg:grid-cols-[1fr_2.2fr_1fr]">
      {[
        ["10%", "Human-in", "Set the result, context, approved data, permissions, and decision gates.", "border-orange-300", "text-orange-300"],
        ["80%", "AI heavy lifting", "Research, extract, compare, draft, process, and prepare the reviewable result.", "border-sky-300", "text-sky-300"],
        ["10%", "Human-after", "Check the Definition of Done, resolve exceptions, approve, and feed learning back into the Skill.", "border-emerald-300", "text-emerald-300"],
      ].map(([share, label, body, border, color]) => (
        <div key={label} className={`border-t-4 ${border} bg-white/[0.05] p-5 sm:p-6`}>
          <p className={`font-serif text-4xl font-semibold ${color}`}>{share}</p>
          <h2 className="mt-3 text-xl font-bold text-white">{label}</h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-300">{body}</p>
        </div>
      ))}
    </div>

    <div className="mt-6 flex items-start gap-3 border-l-4 border-orange-400 bg-white/[0.05] p-4 text-sm leading-relaxed text-slate-200">
      <Scale className="mt-0.5 h-5 w-5 flex-shrink-0 text-orange-300" />
      <p>Put approval at the point where judgement occurs. One sign-off after a long run may reveal that something is wrong without revealing where it went wrong.</p>
    </div>
  </div>
);

const ImportFlowSlide = () => {
  const stages = [
    ["1", "Deterministic", "Validate", "Schema, types, required fields, and exact row count."],
    ["2", "AI", "Suggest", "Propose mappings only for ambiguous fields, with evidence."],
    ["3", "Human", "Approve", "Accept, correct, or reject ambiguous mappings and exceptions."],
    ["4", "Deterministic", "Dry run", "Apply explicit rules without changing production data."],
    ["5", "Deterministic", "Import", "Use an idempotent transaction that is safe to retry."],
    ["6", "Deterministic", "Reconcile", "Account for every imported, rejected, and quarantined row."],
  ];

  const ownerStyle: Record<string, string> = {
    AI: "text-sky-300",
    Human: "text-orange-300",
    Deterministic: "text-emerald-300",
  };

  return (
    <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col justify-center py-7 sm:py-10">
      <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-orange-300">Worked boundary · data import</p>
      <h1 className="mt-4 max-w-5xl font-serif text-4xl font-semibold leading-tight tracking-[-0.04em] text-white sm:text-5xl lg:text-6xl">
        Import data on deterministic rails.
      </h1>
      <p className="mt-4 max-w-4xl text-lg leading-relaxed text-slate-300">
        AI can help interpret ambiguity. It should not silently own the transaction, identity rules, or proof that every record landed where it belongs.
      </p>

      <ol className="mt-7 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {stages.map(([number, owner, title, body]) => (
          <li key={number} className="border-l-2 border-white/20 bg-white/[0.05] p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-black text-white">{number}</span>
              <span className={`text-[11px] font-extrabold uppercase tracking-[0.14em] ${ownerStyle[owner]}`}>{owner}</span>
            </div>
            <h2 className="mt-4 text-xl font-bold text-white">{title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">{body}</p>
          </li>
        ))}
      </ol>

      <div className="mt-6 flex items-center gap-3 rounded-2xl border border-emerald-300/25 bg-emerald-300/10 p-4 text-emerald-50">
        <Database className="h-5 w-5 flex-shrink-0 text-emerald-300" />
        <p className="font-mono text-sm font-bold sm:text-base">input rows = imported + rejected + quarantined</p>
      </div>
    </div>
  );
};

const renderCustomSlide = (slide: GuideSlide, context: { light: boolean; printMode: boolean }) => {
  if (slide.id === "step-gears") return <GearsOverviewSlide />;
  if (slide.id === "step-skills") return <SkillsVideoSlide printMode={context.printMode} />;
  if (slide.id === "skill-apps") return <SkillAppsSlide />;
  if (slide.id === "dmp") return <DmpSlide />;
  if (slide.id === "step-boundaries") return <TenEightyTenSlide />;
  if (slide.id === "deterministic-import") return <ImportFlowSlide />;

  const gearNumber = ({
    "gear-chat": 1,
    "gear-assistant": 2,
    "gear-agents": 3,
    "gear-loops": 4,
  } as Record<string, number>)[slide.id];

  const gear = FOUR_GEARS.find((candidate) => candidate.n === gearNumber);
  if (gear) return <GearDetailSlide gear={gear} light={context.light} />;
  return null;
};

const LonghandAIOverview = () => (
  <SlideGuide
    name="Longhand AI Overview"
    trackingName="Longhand AI Overview Slide Guide"
    canonicalPath="/ai-30/program/overview"
    storageKey="longhand-ai-overview-completed-steps"
    slideStorageKey="longhand-ai-overview-last-slide"
    seo={{
      title: "Longhand AI Overview: Gears, Responsible AI, TDAR, and DMP",
      description: "Choose the right AI gear, package reusable Agent Skills, apply responsible AI controls, TDAR, and the DMP, and keep human judgement and deterministic execution visible.",
      keywords: ["Longhand AI", "4 Gears of AI Usage", "Agent Skills", "Responsible AI", "TDAR", "Decision Making Process", "human judgement", "deterministic AI workflows"],
    }}
    schema={{
      description: "A seven-step overview for choosing valuable AI work, matching it to the right gear, applying responsible AI controls, defining a measurable result with TDAR, making the decision with the DMP, and keeping human and deterministic boundaries visible.",
      totalTime: "PT68M",
      supplies: ["One real workflow or recurring task", "A note-taking surface"],
    }}
    cover={{
      kicker: "Longhand AI",
      title: "Longhand AI",
      accent: "Overview",
      description: "Choose the work. Set the controls. Define the result. Make the decision. Keep human judgement and exact execution visible.",
      badges: ["7 decisions", "About 70 minutes", "25 slides", "Progress saved"],
      finishLineTitle: "One AI Work Decision Card",
      finishLineBody: "You will leave with one real workflow classified, measured, bounded, and ready for a build — or an intentional decision not to use AI.",
    }}
    outcomes={{
      eyebrow: "The seven decisions",
      heading: "Turn AI possibility into one accountable work decision.",
      items: [
        "Match the work to the right gear",
        "Decide whether the procedure should become a Skill",
        "Turn responsible-AI principles into operating controls",
        "Define the result, baseline, contributions, and remeasurement with TDAR",
        "Make a challengeable Big What, Why, What, How, and What If decision",
        "Separate AI work from deterministic execution",
        "Place human judgement, review, and accountability",
      ],
      summary: "The output is not another list of AI ideas. It is one reviewable decision about work that matters, how it should run, and who remains accountable.",
    }}
    roadmapHeading="Choose the gear. Package the expertise. Set the controls. Define the result. Make the decision. Keep the boundaries visible."
    steps={LONGHAND_OVERVIEW_STEPS}
    slides={LONGHAND_OVERVIEW_SLIDES}
    contentsExtras={[
      { slideId: "step-gears", label: "The 4 Gears" },
      { slideId: "step-skills", label: "Future of Work video" },
      { slideId: "responsible-ai-model", label: "Responsible AI" },
      { slideId: "tdar-model", label: "TDAR" },
      { slideId: "dmp", label: "DMP" },
      { slideId: "deterministic-import", label: "Deterministic import" },
      { slideId: "step-decision-card", label: "Decision Card" },
    ]}
    renderCustomSlide={renderCustomSlide}
    finish={{
      eyebrow: "The overview is complete",
      heading: "You know what to build — and what not to.",
      body: "Keep the card with the work. Use the audit to identify the next capability gap, then choose the training that helps you shift up without hiding the judgement or control the result requires.",
      cards: [
        {
          eyebrow: "Assess",
          title: "Take the AI Tune-Up",
          body: "Rate the evidence you have today and open the exact training for each red or yellow gap.",
          href: "/ai-30/program/ai-tune-up",
        },
        {
          eyebrow: "Set up",
          title: "AI Agent Starter",
          body: "Choose a home base, set the data boundary, connect work, and save a reusable workflow.",
          href: "/ai-agent-starter",
        },
        {
          eyebrow: "Build",
          title: "Build your first Skill",
          body: "Turn the chosen job, workflow, toolbox, quality criteria, and stop rules into a reusable Skill.",
          href: "/s2p-elevate-ai-guide#part4",
        },
      ],
    }}
  />
);

export default LonghandAIOverview;
